import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DataCard } from '@/components/ui/data-card'
import { PageHeader } from '@/components/ui/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { useSettings } from '@/hooks/useSettings'
import { ColumnDef } from '@tanstack/react-table'
import { 
  Settings as SettingsIcon, 
  Key, 
  Shield, 
  Bell, 
  Plus,
  Copy,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react'
import { format } from 'date-fns'
import { useToast } from '@/hooks/use-toast'

interface ApiKey {
  id: string
  name: string
  key: string
  permissions: string[]
  status: 'ACTIVE' | 'INACTIVE'
  lastUsed?: string
  createdAt: string
}

interface BlacklistItem {
  id: string
  type: 'PHONE' | 'KEYWORD'
  value: string
  reason: string
  createdAt: string
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState('api')
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false)
  const [showBlacklistDialog, setShowBlacklistDialog] = useState(false)
  const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({})

  const { toast } = useToast()
  const { data: settings, isLoading: settingsLoading } = useSettings()

  // Mock data - 实际项目中应该从API获取
  const apiKeys: ApiKey[] = [
    {
      id: '1',
      name: '生产环境密钥',
      key: 'sk_live_1234567890abcdef',
      permissions: ['sms:send', 'sms:query'],
      status: 'ACTIVE',
      lastUsed: '2024-01-15 14:30:00',
      createdAt: '2024-01-01 10:00:00'
    },
    {
      id: '2', 
      name: '测试环境密钥',
      key: 'sk_test_abcdef1234567890',
      permissions: ['sms:send'],
      status: 'INACTIVE',
      createdAt: '2024-01-10 15:20:00'
    }
  ]

  const blacklist: BlacklistItem[] = [
    {
      id: '1',
      type: 'PHONE',
      value: '13800138000',
      reason: '用户投诉',
      createdAt: '2024-01-10 09:30:00'
    },
    {
      id: '2',
      type: 'KEYWORD', 
      value: '垃圾',
      reason: '敏感词汇',
      createdAt: '2024-01-08 16:45:00'
    }
  ]

  const handleCopyApiKey = (key: string) => {
    navigator.clipboard.writeText(key)
    toast({
      title: '复制成功',
      description: 'API密钥已复制到剪贴板',
    })
  }

  const toggleApiKeyVisibility = (id: string) => {
    setShowApiKey(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const apiKeyColumns: ColumnDef<ApiKey>[] = [
    {
      accessorKey: 'name',
      header: '密钥名称',
    },
    {
      accessorKey: 'key',
      header: 'API密钥',
      cell: ({ row }) => {
        const apiKey = row.original
        const isVisible = showApiKey[apiKey.id]
        const maskedKey = apiKey.key.substring(0, 8) + '...' + apiKey.key.substring(apiKey.key.length - 8)
        
        return (
          <div className="flex items-center space-x-2">
            <span className="font-mono text-sm">
              {isVisible ? apiKey.key : maskedKey}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleApiKeyVisibility(apiKey.id)}
            >
              {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopyApiKey(apiKey.key)}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        )
      },
    },
    {
      accessorKey: 'permissions',
      header: '权限',
      cell: ({ row }) => {
        const permissions = row.getValue('permissions') as string[]
        return (
          <div className="flex gap-1">
            {permissions.slice(0, 2).map((permission, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {permission}
              </Badge>
            ))}
            {permissions.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{permissions.length - 2}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: '状态',
      cell: ({ row }) => {
        const status = row.getValue('status') as string
        return (
          <Badge variant={status === 'ACTIVE' ? 'success' : 'secondary'}>
            {status === 'ACTIVE' ? '启用' : '禁用'}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'lastUsed',
      header: '最后使用',
      cell: ({ row }) => {
        const lastUsed = row.getValue('lastUsed') as string
        return lastUsed ? format(new Date(lastUsed), 'yyyy-MM-dd HH:mm') : '从未使用'
      },
    },
    {
      accessorKey: 'createdAt',
      header: '创建时间',
      cell: ({ row }) => {
        return format(new Date(row.getValue('createdAt')), 'yyyy-MM-dd HH:mm')
      },
    },
    {
      id: 'actions',
      header: '操作',
      cell: ({ row }) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )
      },
    },
  ]

  const blacklistColumns: ColumnDef<BlacklistItem>[] = [
    {
      accessorKey: 'type',
      header: '类型',
      cell: ({ row }) => {
        const type = row.getValue('type') as string
        return (
          <Badge variant={type === 'PHONE' ? 'default' : 'secondary'}>
            {type === 'PHONE' ? '手机号' : '关键词'}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'value',
      header: '内容',
      cell: ({ row }) => {
        const value = row.getValue('value') as string
        const type = row.original.type
        return (
          <span className={type === 'PHONE' ? 'font-mono' : ''}>
            {type === 'PHONE' ? value.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : value}
          </span>
        )
      },
    },
    {
      accessorKey: 'reason',
      header: '原因',
    },
    {
      accessorKey: 'createdAt',
      header: '添加时间',
      cell: ({ row }) => {
        return format(new Date(row.getValue('createdAt')), 'yyyy-MM-dd HH:mm')
      },
    },
    {
      id: 'actions',
      header: '操作',
      cell: ({ row }) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )
      },
    },
  ]

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">配置管理</h2>
        <Button onClick={() => refetchApiKeys()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          刷新
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="api">API密钥</TabsTrigger>
          <TabsTrigger value="blacklist">黑名单管理</TabsTrigger>
          <TabsTrigger value="callback">回调配置</TabsTrigger>
          <TabsTrigger value="security">安全设置</TabsTrigger>
        </TabsList>

        <TabsContent value="api" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Key className="mr-2 h-5 w-5" />
                  API密钥管理
                </div>
                <Dialog open={showApiKeyDialog} onOpenChange={setShowApiKeyDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" />
                      创建密钥
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>创建API密钥</DialogTitle>
                      <DialogDescription>
                        创建新的API密钥用于接口调用
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="keyName">密钥名称</Label>
                        <Input
                          id="keyName"
                          placeholder="请输入密钥名称"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>权限设置</Label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Switch id="send-sms" />
                            <Label htmlFor="send-sms">短信发送</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch id="query-records" />
                            <Label htmlFor="query-records">记录查询</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch id="manage-templates" />
                            <Label htmlFor="manage-templates">模板管理</Label>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setShowApiKeyDialog(false)}>
                          取消
                        </Button>
                        <Button onClick={() => setShowApiKeyDialog(false)}>
                          创建
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardTitle>
              <CardDescription>
                管理您的API密钥和权限设置
              </CardDescription>
            </CardHeader>
            <CardContent>
              {false ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-sm text-muted-foreground">加载中...</div>
                </div>
              ) : (
                <DataTable
                  columns={apiKeyColumns}
                  data={apiKeys}
                  searchKey="name"
                  searchPlaceholder="搜索密钥名称..."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blacklist" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Shield className="mr-2 h-5 w-5" />
                  黑名单管理
                </div>
                <Dialog open={showBlacklistDialog} onOpenChange={setShowBlacklistDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" />
                      添加黑名单
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>添加黑名单</DialogTitle>
                      <DialogDescription>
                        添加手机号或关键词到黑名单
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>类型</Label>
                        <div className="flex space-x-4">
                          <div className="flex items-center space-x-2">
                            <input type="radio" id="phone" name="type" value="phone" />
                            <Label htmlFor="phone">手机号</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input type="radio" id="keyword" name="type" value="keyword" />
                            <Label htmlFor="keyword">关键词</Label>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="value">内容</Label>
                        <Input
                          id="value"
                          placeholder="请输入手机号或关键词"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reason">原因</Label>
                        <Textarea
                          id="reason"
                          placeholder="请输入添加原因"
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setShowBlacklistDialog(false)}>
                          取消
                        </Button>
                        <Button onClick={() => setShowBlacklistDialog(false)}>
                          添加
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardTitle>
              <CardDescription>
                管理手机号和关键词黑名单
              </CardDescription>
            </CardHeader>
            <CardContent>
              {false ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-sm text-muted-foreground">加载中...</div>
                </div>
              ) : (
                <DataTable
                  columns={blacklistColumns}
                  data={blacklist}
                  searchKey="value"
                  searchPlaceholder="搜索黑名单内容..."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="callback" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell className="mr-2 h-5 w-5" />
                回调配置
              </CardTitle>
              <CardDescription>
                配置短信状态回调和通知设置
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>启用状态回调</Label>
                    <p className="text-sm text-muted-foreground">
                      接收短信发送状态的实时回调通知
                    </p>
                  </div>
                  <Switch defaultChecked={settings?.callback?.enabled || false} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="callbackUrl">回调地址</Label>
                  <Input
                    id="callbackUrl"
                    placeholder="https://your-domain.com/callback"
                    defaultValue={settings?.webhook?.url || ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="secret">签名密钥</Label>
                  <Input
                    id="secret"
                    type="password"
                    placeholder="用于验证回调请求的签名"
                    defaultValue={settings?.webhook?.secret || ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label>回调事件</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch id="delivery-status" defaultChecked />
                      <Label htmlFor="delivery-status">送达状态</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="send-failure" defaultChecked />
                      <Label htmlFor="send-failure">发送失败</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="uplink-message" />
                      <Label htmlFor="uplink-message">上行消息</Label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button>保存配置</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="mr-2 h-5 w-5" />
                安全设置
              </CardTitle>
              <CardDescription>
                配置账户安全和访问控制
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>IP白名单验证</Label>
                    <p className="text-sm text-muted-foreground">
                      只允许白名单内的IP地址访问API
                    </p>
                  </div>
                  <Switch defaultChecked={settings?.security?.enableIpWhitelist || false} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ipWhitelist">IP白名单</Label>
                  <Textarea
                    id="ipWhitelist"
                    placeholder="每行一个IP地址或IP段，例如：192.168.1.1 或 192.168.1.0/24"
                    defaultValue={settings?.security?.ipWhitelist?.join('\n') || ''}
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>发送频率限制</Label>
                      <p className="text-sm text-muted-foreground">
                        限制API调用频率，防止滥用
                      </p>
                    </div>
                    <Switch defaultChecked={settings?.security?.enableRateLimit || true} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="rateLimit">每分钟限制</Label>
                      <Input
                        id="rateLimit"
                        type="number"
                        placeholder="100"
                        defaultValue={settings?.security?.rateLimit || 100}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="burstLimit">突发限制</Label>
                      <Input
                        id="burstLimit"
                        type="number"
                        placeholder="200"
                        defaultValue={settings?.security?.burstLimit || 200}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button>保存设置</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}